import React from 'react';
import styled from '@emotion/styled';
import { Link } from 'react-router-dom';
import { siteConfig } from '../config/site';

const DesktopNav = styled.nav`
  display: none;
  gap: 2rem;
  align-items: center;

  @media (min-width: 768px) {
    display: flex;
  }

  @media (min-width: 1024px) {
    gap: 2.5rem;
  }
`;

const NavLink = styled.a`
  color: ${({ isScrolled }) =>
    isScrolled ? '#374151' : 'rgba(255, 255, 255, 0.9)'};
  font-weight: 500;
  font-size: 1rem;
  text-decoration: none;
  transition: all 0.3s ease;
  position: relative;
  padding: 0.5rem 0;

  &:hover {
    color: ${({ isScrolled }) => (isScrolled ? '#7c3aed' : 'white')};
    transform: translateY(-1px);
  }

  /* Subtle underline effect on hover */
  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: ${({ isScrolled }) =>
      isScrolled
        ? 'linear-gradient(45deg, #7c3aed, #ec4899)'
        : 'rgba(255, 255, 255, 0.8)'};
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }

  @media (min-width: 1024px) {
    font-size: 1rem;
  }
`;

const CTAButton = styled.a`
  background: ${({ isScrolled }) =>
    isScrolled
      ? 'linear-gradient(45deg, #7c3aed, #ec4899)'
      : 'rgba(255, 255, 255, 0.2)'};
  color: white;
  padding: 0.75rem 1.5rem;
  border-radius: 50px;
  font-weight: 600;
  font-size: 1.5rem;
  text-decoration: none;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  backdrop-filter: ${({ isScrolled }) => (isScrolled ? 'none' : 'blur(8px)')};
  border: ${({ isScrolled }) =>
    isScrolled ? 'none' : '1px solid rgba(255, 255, 255, 0.3)'};
  box-shadow: ${({ isScrolled }) =>
    isScrolled
      ? '0 4px 15px rgba(124, 58, 237, 0.3)'
      : '0 4px 15px rgba(0, 0, 0, 0.1)'};

  &:hover {
    transform: translateY(-2px) scale(1.05);
    box-shadow: ${({ isScrolled }) =>
      isScrolled
        ? '0 8px 25px rgba(124, 58, 237, 0.4)'
        : '0 8px 25px rgba(0, 0, 0, 0.15)'};
    background: ${({ isScrolled }) =>
      isScrolled
        ? 'linear-gradient(45deg, #6d28d9, #db2777)'
        : 'rgba(255, 255, 255, 0.3)'};
  }

  &:active {
    transform: translateY(-1px) scale(1.02);
  }

  @media (min-width: 1024px) {
    padding: 0.875rem 2rem;
    font-size: 1.5rem;
  }
`;

const NavItems = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;

  @media (min-width: 1024px) {
    gap: 2.5rem;
  }
`;

const NavSeparator = styled.div`
  width: 1px;
  height: 24px;
  background: ${({ isScrolled }) =>
    isScrolled ? 'rgba(55, 65, 81, 0.2)' : 'rgba(255, 255, 255, 0.2)'};
  margin: 0 0.5rem;

  @media (min-width: 1024px) {
    margin: 0 1rem;
  }
`;

export default function Navigation({ isScrolled, isHomePage }) {
  const scrollToSection = (e, id) => {
    e.preventDefault();

    // If we're not on the home page and trying to access a section via #,
    // navigate to home page first
    if (!isHomePage && id.startsWith('#')) {
      window.location.href = '/' + id;
      return;
    }

    const sectionId = id.replace('#', '');
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }
  };

  // Separate regular nav items from CTA
  const regularNavItems = siteConfig.navItems.filter(
    (item) => item.label !== 'Try It Out',
  );
  const ctaItem = siteConfig.navItems.find(
    (item) => item.label === 'Try It Out',
  );

  return (
    <DesktopNav>
      <NavItems>
        {regularNavItems.map((item) => (
          <NavLink
            key={item.label}
            href={item.href}
            isScrolled={isScrolled}
            onClick={(e) =>
              item.href.startsWith('#') && scrollToSection(e, item.href)
            }
          >
            {item.label}
          </NavLink>
        ))}
      </NavItems>

      {ctaItem && (
        <>
          <NavSeparator isScrolled={isScrolled} />
          <CTAButton as={Link} to={ctaItem.href} isScrolled={isScrolled}>
            {ctaItem.label}
          </CTAButton>
        </>
      )}
    </DesktopNav>
  );
}
